import { StrictMode } from 'react'
import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider } from '@clerk/clerk-react'
import { ptBR, enUS, esMX } from '@clerk/localizations'
import { CLERK_PUBLISHABLE_KEY } from './config/clerk'
import { initSentry } from './lib/sentry'
import { initWebVitals } from './lib/web-vitals'
import { resolveInitialLocale } from './lib/locale-detection'
import { registrarServiceWorkerDeAviso } from './lib/os-notification/aviso-do-sistema'
import App from './App'
import './styles/index.css'

// Pick the Clerk SignIn / SignUp localization that matches the AxiomGO
// Harness V1 locale (pt-BR | en-US | es-419). Resolved at boot so the
// sign-in screen renders in the user's language on first paint — Clerk
// does not re-localize a mounted SignIn component when the prop changes,
// so a remount-on-language-switch would be needed for runtime override.
// Trade-off accepted V1: language is locked to the boot-time resolution.
const BOOTSTRAP_SUPPORTED = ['pt-BR', 'en-US', 'es-419']
const initialLocale = resolveInitialLocale(localStorage, navigator, BOOTSTRAP_SUPPORTED)
const clerkLocalization =
  initialLocale === 'pt-BR' ? ptBR : initialLocale === 'es-419' ? esMX : enUS

// Sentry must be initialised before anything else renders
initSentry()

const root = document.getElementById('app')
if (!root) throw new Error('Root element #app not found')

// React 19 error hooks route all error categories to Sentry — but ONLY when a DSN
// is configured. Without a DSN, Sentry is a no-op, so building the error handlers
// at boot just widens the boot surface for no benefit. Gating them keeps behaviour
// identical when a DSN IS set and shrinks the critical boot path otherwise.
const sentryReactErrorHandler = import.meta.env.VITE_SENTRY_DSN
  ? Sentry.reactErrorHandler()
  : undefined

createRoot(root, {
  onUncaughtError: sentryReactErrorHandler,
  onCaughtError: sentryReactErrorHandler,
  onRecoverableError: sentryReactErrorHandler,
}).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      localization={clerkLocalization}
      telemetry={{ disabled: true }}
    >
      <App />
    </ClerkProvider>
  </StrictMode>,
)

// Web Vitals RUM — called after render, never inside a component, returns void synchronously.
// All async work is handled internally via web-vitals callbacks.
initWebVitals()

// STO-354/STO-359 — registra o service worker de aviso do SISTEMA OPERACIONAL.
// SÓ registro: a permissão em si só pode ser pedida por gesto do usuário
// (ver SettingsModal), nunca aqui no boot.
void registrarServiceWorkerDeAviso()
